import { useEffect, useState } from "react";
import { Bell, FileText, KeyRound, LogOut, Phone, Repeat, UserRound, Wallet } from "lucide-react";
import { Link, Outlet, useLocation } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { listNotices, listStagedUploads } from "@/features/portal/api";
import { portalHref, portalSignInHref } from "@/features/portal/portal-base";
import { usePortalSessionStore } from "@/features/portal/store/portal-session-store";
import { usePortalLogout } from "@/features/portal/use-portal-logout";
import type { PortalBranding, PortalClientSummary } from "@/features/portal/types";
import { cn } from "@/lib/utils";

function initials(name: string) {
  return (
    name
      .split(" ")
      .map((part) => part[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?"
  );
}

const NAV_LINK =
  "text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors";
const NAV_LINK_ACTIVE = "bg-brand-panel text-white hover:bg-brand-panel hover:text-white";

/** Open-notice count for the Notices nav badge. Runs only while the authenticated
 * nav is mounted; failures are silent (the badge just doesn't show). */
function useOpenNoticeCount() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let active = true;
    listNotices()
      .then((list) => active && setCount(list.filter((n) => n.status !== "RESOLVED").length))
      .catch(() => {
        /* no badge on failure */
      });
    return () => {
      active = false;
    };
  }, []);
  return count;
}

/** Section tabs (left cluster), shown only once a client session is bound.
 * Mirrors the friendlier client-facing labels in the portal design. Active state
 * is matched on the URL *section* (so e.g. a business-worksheet detail still
 * highlights My finances) rather than the exact link target. */
function PortalSectionNav({ client }: { client: PortalClientSummary }) {
  const { pathname } = useLocation();
  const openNotices = useOpenNoticeCount();
  const link = (active: boolean) => cn(NAV_LINK, active && NAV_LINK_ACTIVE);
  return (
    <nav className="hidden items-center gap-1 sm:flex">
      <Link to={portalHref("/home")} className={link(pathname.endsWith("/home"))}>
        Home
      </Link>
      {client.showDocuments && (
        <Link
          to={portalHref("/documents")}
          // "requested-documents" has no "/documents" segment (it's hyphenated),
          // so this matches only the real Documents routes.
          className={link(pathname.includes("/documents"))}
        >
          My documents
        </Link>
      )}
      {client.showFinancials && (
        <Link
          to={portalHref("/financials/personal")}
          className={link(pathname.includes("/financials"))}
        >
          My finances
        </Link>
      )}
      <Link to={portalHref("/notices")} className={link(pathname.includes("/notices"))}>
        Notices
        {openNotices > 0 && (
          <span className="bg-primary text-primary-foreground grid h-5 min-w-5 place-items-center rounded-full px-1.5 text-xs font-bold">
            {openNotices}
          </span>
        )}
      </Link>
    </nav>
  );
}

/** Account menu (right cluster): client identity + account actions + sign out.
 * Also carries the section links as menu items so they stay reachable on mobile
 * where the inline section nav is hidden, and the secondary surfaces (Profile,
 * Proposals, Requested) that aren't in the top nav. */
function PortalAccountMenu({ client }: { client: PortalClientSummary }) {
  const logout = usePortalLogout();
  const [warnCount, setWarnCount] = useState<number | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);

  async function finishLogout() {
    setLoggingOut(true);
    await logout();
  }

  // Guard sign-out behind the unsent-uploads check (previously owned by the home
  // hub) so no path logs out while staged files are still unsent.
  async function attemptLogout() {
    try {
      const staged = await listStagedUploads();
      if (staged.length > 0) {
        setWarnCount(staged.length);
        return;
      }
    } catch {
      /* if we can't check, just log out */
    }
    void finishLogout();
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Account menu"
            className="hover:bg-muted flex items-center gap-2 rounded-full py-1 pr-1 pl-2 transition-colors"
          >
            <span className="hidden max-w-[160px] truncate text-sm font-medium sm:block">
              {client.name}
            </span>
            <Avatar className="size-8">
              <AvatarFallback className="bg-accent text-accent-foreground text-xs font-bold">
                {initials(client.name)}
              </AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="truncate">{client.name}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to={portalHref("/profile")}>
              <UserRound /> Profile
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to={portalHref("/notices")}>
              <Bell /> Notices
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to={portalHref("/proposals")}>
              <FileText /> Proposals
            </Link>
          </DropdownMenuItem>
          {/* Requested docs are a firm→client request the client must fulfil —
              always visible, independent of the published-docs toggle below. */}
          <DropdownMenuItem asChild>
            <Link to={portalHref("/requested-documents")}>
              <FileText /> Requested
            </Link>
          </DropdownMenuItem>
          {client.showDocuments && (
            <DropdownMenuItem asChild>
              <Link to={portalHref("/documents")}>
                <FileText /> Documents
              </Link>
            </DropdownMenuItem>
          )}
          {client.showFinancials && (
            <DropdownMenuItem asChild>
              <Link to={portalHref("/financials/personal")}>
                <Wallet /> Financial info
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild>
            <Link to={portalHref("/password")}>
              <KeyRound /> Change password
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to={portalHref("/switch-client")}>
              <Repeat /> Switch client
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem variant="destructive" onSelect={() => void attemptLogout()}>
            <LogOut /> Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog
        open={warnCount !== null}
        onOpenChange={(open) => {
          if (!open) setWarnCount(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>You have unsent files</AlertDialogTitle>
            <AlertDialogDescription>
              You have {warnCount} unsent {warnCount === 1 ? "file" : "files"} — leave anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loggingOut}>Stay</AlertDialogCancel>
            <AlertDialogAction
              disabled={loggingOut}
              onClick={(e) => {
                e.preventDefault();
                void finishLogout();
              }}
            >
              Leave anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/**
 * Public, firm-branded shell for every portal page. Branding (name/logo/footers)
 * comes from the resolved firm (Settings 5.5 `portal_*`), falling back to the
 * persisted session branding. Only the authenticated route group passes
 * ``authed`` — that (not the persisted session alone) turns on the section nav +
 * account menu, so the public pages (login/resolve/onboarding) stay minimal even
 * for a visitor who still has a session in localStorage. NOT gated by staff JWT.
 */
export function PortalLayout({
  branding,
  authed = false,
}: {
  branding?: PortalBranding | null;
  authed?: boolean;
}) {
  const sessionBranding = usePortalSessionStore((s) => s.branding);
  const client = usePortalSessionStore((s) => s.client);
  const portalName = usePortalSessionStore((s) => s.portalName);
  const b = branding ?? sessionBranding;
  const showNav = authed && !!client;

  // Brand click: home on authed surfaces, else the firm's sign-in (or portal root).
  const brandTo = showNav ? portalHref("/home") : portalSignInHref(portalName ?? b?.portalName);

  return (
    <div className="bg-background text-foreground flex min-h-screen flex-col">
      <header className="bg-card/90 sticky top-0 z-10 flex items-center justify-between gap-4 border-b px-6 py-3 backdrop-blur">
        <div className="flex min-w-0 items-center gap-6">
          <Link to={brandTo} className="flex items-center gap-3" aria-label="Portal home">
            {b?.logoUrl ? (
              <img src={b.logoUrl} alt="" className="h-8 max-w-[160px] object-contain" />
            ) : null}
            {(!b || b.showName) && (
              <span className="truncate font-semibold">{b?.firmName || "Client Portal"}</span>
            )}
          </Link>
          {showNav && client ? <PortalSectionNav client={client} /> : null}
        </div>
        <div className="flex items-center gap-2">
          {showNav && b?.phone ? (
            <a
              href={`tel:${b.phone}`}
              className="border-input bg-background hover:bg-accent hover:text-accent-foreground hidden items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors sm:flex"
            >
              <Phone className="text-success size-4" />
              Need help? Call us
            </a>
          ) : null}
          {showNav && client ? <PortalAccountMenu client={client} /> : null}
        </div>
      </header>
      <main className="mx-auto w-full max-w-6xl flex-1 px-6 py-8">
        <Outlet />
      </main>
      {(b?.footerLeft || b?.footerRight) && (
        <footer className="text-muted-foreground flex items-center justify-between border-t px-6 py-3 text-sm">
          <span>{b?.footerLeft}</span>
          <span>{b?.footerRight}</span>
        </footer>
      )}
    </div>
  );
}
